import {
  Links,
  Meta,
  MetaFunction,
  Outlet,
  redirectDocument,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useLocation,
  useRouteLoaderData,
} from "@remix-run/react";
import { PropsWithChildren, useContext, useEffect } from "react";
import i18isoCountries from "i18n-iso-countries";
import enCountries from "i18n-iso-countries/langs/en.json";
import {
  ActionFunction,
  LoaderFunctionArgs,
  redirect,
  defer,
} from "@remix-run/node";

import DefaultErrorBoundary from "./components/DefaultErrorBoundary";

import ThemeContext, { ThemeContextProvider } from "./contexts/ThemeContext";
import { defaultTemplate } from "./theme/burgerfuel";

// Font Awesome
import { config } from "@fortawesome/fontawesome-svg-core";
config.autoAddCss = false;
import "@fortawesome/fontawesome-svg-core/styles.css";

import "./theme/global.css";
import Api from "./api/api";
import { ModalContextProvider } from "./contexts/ModalContext";
import {
  destroySession,
  getSession,
  commitSession,
  getAuthSession,
  commitAuthSession,
  destroyAuthSession,
  getPersistentSession,
} from "./sessions";
import { AuthContextProvider } from "./contexts/AuthContext";
import { SettingsContextProvider } from "./contexts/SettingsContext";
import { uploadcareConfig } from "./configs/uploadcareConfig";
import { makeAuthHeaders } from "~/api/endpoints/Auth";
import { GoogleContextProvider } from "./contexts/GoogleContext";
import { withSentry } from "@sentry/remix";
import ErrorModal from "~/components/ErrorModal/ErrorModal";
import { AssetInfo, AuthenticatedUser } from "~/entities/authEntities";
import { isPaymentPage } from "~/helpers/routesHelpers";
import { NavBarSize } from "./theme/models/themeModels";
import { appConfig } from "./configs/appConfig";
import { sanitizeGtmId } from "~/helpers/sanitizeHelpers";
import { addMinutes, addSeconds, isBefore } from "date-fns";
import { apiConfig } from "~/configs/apiConfig";
import WebViewSessionExpiredPopup from "~/components/WebViewSessionExpiredPopup/WebViewSessionExpiredPopup";

i18isoCountries.registerLocale(enCountries);

export const meta: MetaFunction<typeof loader> = ({ data, error }) => {
  const faviconUrl = data?.domainSettings?.favicon
    ? uploadcareConfig.getUrl(
        data?.domainSettings?.favicon?.fileId,
        data?.domainSettings?.favicon?.modifiers,
      )
    : "/favicon.ico";

  return [
    {
      tagName: "link",
      rel: "icon",
      href: faviconUrl,
    },
    {
      title: error
        ? "Error - Something went wrong!"
        : data?.domainSettings?.title
          ? data?.domainSettings?.title
          : data?.orgName
            ? `${data?.orgName} - Online Ordering`
            : "Online ordering",
    },
    {
      name: "description",
      content:
        error || !data?.domainSettings?.description
          ? "Online ordering"
          : data?.domainSettings?.description,
    },
  ];
};

export const action: ActionFunction = async ({ request }) => {
  const session = await getSession(request.headers.get("Cookie"));
  const authSession = await getAuthSession(request.headers.get("Cookie"));
  const formData = await request.formData();

  const orderSource = formData.get("orderSource");
  const siteId = formData.get("siteId");

  const homeUrl = orderSource && siteId ? `/${orderSource}/${siteId}` : "/";

  await destroySession(session);
  await destroyAuthSession(authSession);
  const newSession = await getSession();
  const newAuthSession = await getAuthSession();
  return redirectDocument(homeUrl, {
    headers: [
      ["Set-Cookie", await commitSession(newSession)],
      ["Set-Cookie", await commitAuthSession(newAuthSession)],
    ],
  });
};

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get("Cookie"));
  const authSession = await getAuthSession(request.headers.get("Cookie"));
  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );

  const authenticatedUserId = authSession.get("authenticatedUserId");
  let accessToken = authSession.get("accessToken");

  const orderId = session.get("orderId");
  const prevSite = persistentSession.get("prevSite");

  const url = new URL(request.url);
  const tempToken = url.searchParams.get("tempToken");

  let customerPromise:
    | Promise<{ customer?: AuthenticatedUser; error?: any }>
    | undefined;
  let assetsPromise: Promise<AssetInfo & { error?: string }> | undefined;

  if (appConfig.WebView && tempToken) {
    try {
      const requestTime = new Date();

      const { authenticatedUser, tokens, verifiedEmail, verifiedPhone } =
        await Api.Auth.verifyTempToken(request, {
          tempToken,
          orderId,
          siteId: prevSite,
        });

      authSession.set("accessToken", tokens.access_token);
      authSession.set(
        "accessTokenExpiry",
        addSeconds(requestTime, tokens.expires_in),
      );
      authSession.set("authenticatedUserId", authenticatedUser?.id);
      authSession.set("verifiedEmail", verifiedEmail);
      authSession.set("verifiedPhone", verifiedPhone);

      return redirect(url.pathname, {
        headers: {
          "Set-Cookie": await commitAuthSession(authSession),
        },
      });
    } catch (e) {
      if (typeof e === "object" && e !== null && "userMessage" in e) {
        throw new Error((e as { userMessage: string }).userMessage);
      }
    }
  } else if (authenticatedUserId && accessToken) {
    // Check if token will expire soon
    const tokenExpiry = authSession.get("accessTokenExpiry");
    if (
      apiConfig.TokenRefreshWindowMinutes &&
      tokenExpiry &&
      isBefore(
        tokenExpiry,
        addMinutes(new Date(), apiConfig.TokenRefreshWindowMinutes),
      )
    ) {
      // Attempt refresh
      const authHeaders = makeAuthHeaders(request, accessToken);
      try {
        console.log("Attempting to refresh tokens");
        const requestTime = new Date();
        const tokens = await Api.Auth.refreshToken(request, authHeaders, {
          refresh_token: "",
        });
        console.log("Tokens refreshed");
        authSession.set("accessToken", tokens.access_token);
        authSession.set(
          "accessTokenExpiry",
          addSeconds(requestTime, tokens.expires_in),
        );
        accessToken = tokens.access_token;
      } catch (e: any) {
        if (e.status === 401) {
          console.log("Attempting to refresh tokens returned 401");
        } else {
          console.warn("Error trying to refresh token", e);
        }
      }
    }

    // Make auth headers anew even if token refresh was attempted, we may have a new token now
    const authHeaders = makeAuthHeaders(request, accessToken);

    customerPromise = Api.Auth.getCustomer(
      authenticatedUserId,
      request,
      authHeaders,
    )
      .then((x) => ({ customer: x }))
      .catch((e) => ({
        error: e,
      }));
    assetsPromise = Api.Auth.getCustomerAssets(
      authenticatedUserId,
      request,
      authHeaders,
    ).catch(() => {
      return {
        assets: [],
        purchaseableAssets: [],
        error: "Something went wrong whilst trying to load assets",
      };
    });
  }

  const [domainInfo, template] = await Promise.all([
    Api.Site.getDomainInfo(request),
    Api.Template.getTemplate(request),
  ]);

  let authenticatedUser: AuthenticatedUser | undefined;
  const customerPromiseRes = await customerPromise;
  if (customerPromiseRes?.customer) {
    authenticatedUser = customerPromiseRes.customer;
  } else if (customerPromiseRes?.error) {
    const e = customerPromiseRes?.error;
    if (e?.status === 401) {
      authSession.unset("accessToken");
      authSession.unset("accessTokenExpiry");
      authSession.unset("authenticatedUserId");
      authSession.unset("requiresSignup");
      authSession.unset("comoRequiresSignup");
      authSession.unset("verifiedEmail");
      authSession.unset("verifiedPhone");
    } else {
      throw e;
    }
  }

  return defer(
    {
      template: {
        ...defaultTemplate,
        ...(template ?? defaultTemplate),
        checkoutScreen: {
          ...defaultTemplate.checkoutScreen,
          ...template?.checkoutScreen,
        },
      },
      authenticatedUser,
      requiresSignup: authSession.get("requiresSignup"),
      orgSettings: domainInfo.settings,
      useComoAuth: domainInfo.useComoAuth,
      comoRequiresSignup: authSession.get("comoRequiresSignup"),
      verifiedEmail: authSession.get("verifiedEmail"),
      verifiedPhone: authSession.get("verifiedPhone"),
      domainSettings: domainInfo.domainSettings,
      orgName: domainInfo?.orgName,
      multiSite: domainInfo?.multiSite,
      assetInfo: assetsPromise,
      targetGoogleTagManagerId:
        domainInfo?.domainSettings?.targetGoogleTagManagerId,
    },
    {
      headers: {
        "Set-Cookie": await commitAuthSession(authSession),
      },
    },
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formAction,
  currentUrl,
  actionResult,
  nextUrl,
}) => {
  return (
    actionResult?.forceRevalidation ||
    formAction === "/auth/requestPasswordless" ||
    formAction === "/auth/verify" ||
    formAction === "/auth/signup" ||
    formAction === "/auth/logout" ||
    formAction === "/auth/removeCard" ||
    formAction === "/auth/myAccount" ||
    formAction === "/loyalty/purchaseAsset" ||
    (formAction != undefined &&
      formAction?.includes("/payment") &&
      !actionResult?.preventRevalidation) ||
    currentUrl.pathname.includes("success") ||
    (isPaymentPage(currentUrl.pathname) && !isPaymentPage(nextUrl.pathname))
  );
};

export function Layout({ children }: PropsWithChildren) {
  // Layout also wraps the ErrorBoundary, where useLoaderData throws; this is undefined there instead
  const data = useRouteLoaderData<typeof loader>("root");
  const { template } = useContext(ThemeContext);
  const targetGoogleTagManagerId = sanitizeGtmId(
    data?.targetGoogleTagManagerId,
  );

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0, minimal-ui"
        />
        <Meta />
        <Links />
        {!!targetGoogleTagManagerId && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${targetGoogleTagManagerId}');
            `,
            }}
          />
        )}
      </head>
      <body
        style={{
          ["--app-font-family" as string]: template.global.font,
          ["--header-height" as string]: appConfig.WebView
            ? "0px"
            : NavBarSize[template.navBar.size],
        }}
      >
        {!!targetGoogleTagManagerId && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${targetGoogleTagManagerId}"
              height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
            }}
          />
        )}
        {children}
        <Scripts />
        <ScrollRestoration />
      </body>
    </html>
  );
}

export const ErrorBoundary = DefaultErrorBoundary;

interface CatchErrorData {
  message: string;
}

export function CatchBoundary({
  caught,
}: {
  caught: { status: number; data: CatchErrorData };
}) {
  return (
    <div className="error-boundary">
      <div className="error-content">
        <h1>Error {caught.status}</h1>
        <p>{caught.data?.message || "Something went wrong"}</p>
      </div>
    </div>
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  const template = data?.template;

  useEffect(() => {
    const checkVersion = async () => {
      const res = await fetch("/version", { cache: "no-store" });
      const { version } = await res.json();

      if (version !== window.CLIENT_APP_VERSION && window.CLIENT_APP_VERSION) {
        console.log("New version detected:", version);
        window.location.reload();
      }
    };

    const handler = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  const location = useLocation();
  const isEscapeUrl = location.pathname.includes("/escape/");

  return (
    <ThemeContextProvider template={template || defaultTemplate}>
      <AuthContextProvider
        authenticatedUser={data?.authenticatedUser}
        requiresSignup={data?.requiresSignup}
        customerSignInOptions={data?.orgSettings?.customerSignInOptions}
        useComoAuth={data?.useComoAuth}
        comoRequiresSignup={data?.comoRequiresSignup}
        verifiedEmail={data.verifiedEmail}
        verifiedPhone={data.verifiedPhone}
        assetInfo={data.assetInfo}
      >
        <SettingsContextProvider
          orgSettings={data?.orgSettings}
          multiSite={data?.multiSite}
        >
          <ModalContextProvider>
            <GoogleContextProvider>
              <Outlet />
              <ErrorModal />
              {appConfig.WebView &&
                !data.authenticatedUser?.id &&
                !isEscapeUrl && <WebViewSessionExpiredPopup />}
            </GoogleContextProvider>
          </ModalContextProvider>
        </SettingsContextProvider>
      </AuthContextProvider>
    </ThemeContextProvider>
  );
}

export default withSentry(App);
